/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Check, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "borrame_cookie_consent";
const VERSION = 1; // bump if the consent categories change → re-prompts users.

/**
 * Cookie banner conforme a la Ley 21.719 (Chile).
 *
 * Reglas implementadas:
 *  - Consentimiento opt-in (jamás pre-marcado).
 *  - Botones "Aceptar todo" y "Rechazar todo" tienen el mismo peso visual.
 *  - Categorías separadas: esenciales (siempre on, no togglable), preferencias,
 *    analíticas. No usamos marketing.
 *  - Decisión revocable: el botón en /legal/cookies dispara un CustomEvent
 *    "borrame:reopen-cookie-banner" que vuelve a mostrar el banner.
 *  - El consentimiento se versiona; cambios de categorías invalidan consentimientos previos.
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, preferences: false, analytics: false });

  // Decide on mount whether to surface the banner.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setOpen(true); return; }
      const parsed = JSON.parse(raw);
      if (parsed.version !== VERSION) { setOpen(true); return; }
    } catch {
      setOpen(true);
    }
  }, []);

  // Allow other components (cookies policy page) to re-open the banner.
  useEffect(() => {
    const reopen = () => { setOpen(true); setShowSettings(true); };
    window.addEventListener("borrame:reopen-cookie-banner", reopen);
    return () => window.removeEventListener("borrame:reopen-cookie-banner", reopen);
  }, []);

  const persist = useCallback((choice) => {
    const payload = { version: VERSION, choice, ...choice, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Side-effects per category — gate optional analytics scripts.
    if (choice.analytics) {
      window.dispatchEvent(new CustomEvent("borrame:analytics-opt-in"));
    }
  }, []);

  const acceptAll = () => {
    const next = { essential: true, preferences: true, analytics: true };
    setPrefs(next);
    persist(next);
    setOpen(false);
  };

  const rejectAll = () => {
    const next = { essential: true, preferences: false, analytics: false };
    setPrefs(next);
    persist(next);
    setOpen(false);
  };

  const savePrefs = () => {
    persist(prefs);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          role="dialog"
          aria-label="Configuración de cookies"
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-50 px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
        >
          <div className="pointer-events-auto max-w-5xl mx-auto bg-white border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
            {!showSettings ? (
              // SIMPLE VIEW
              <div className="grid lg:grid-cols-[1fr_auto] gap-5 p-5 sm:p-6 items-start">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="shrink-0 w-10 h-10 bg-[#0B2447] flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-amber-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-gray-900 text-base sm:text-lg">
                      Tu privacidad importa — Ley 21.719
                    </div>
                    <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                      Usamos cookies <b>esenciales</b> (necesarias para tu sesión, no requieren consentimiento)
                      y, si nos autorizas, cookies <b>analíticas</b> para mejorar el sitio.
                      No usamos cookies publicitarias. Lee nuestra{" "}
                      <Link to="/legal/cookies" className="underline">Política de Cookies</Link>{" "}
                      y nuestra{" "}
                      <Link to="/legal/privacidad" className="underline">Política de Privacidad</Link>.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center lg:flex-col lg:items-stretch lg:min-w-[200px]">
                  <button
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                    className="px-5 py-2.5 bg-[#0B2447] hover:bg-[#071932] text-white text-sm font-semibold transition-colors"
                  >
                    Aceptar todo
                  </button>
                  <button
                    onClick={rejectAll}
                    data-testid="cookie-reject-all"
                    className="px-5 py-2.5 bg-white hover:bg-gray-50 text-[#0B2447] text-sm font-semibold border border-[#0B2447] transition-colors"
                  >
                    Rechazar todo
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="cookie-customize"
                    className="px-5 py-2.5 text-gray-600 hover:text-gray-900 text-sm underline transition-colors"
                  >
                    Personalizar
                  </button>
                </div>
              </div>
            ) : (
              // DETAILED VIEW
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-10 h-10 bg-[#0B2447] flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-amber-300" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-gray-900 text-base sm:text-lg">
                        Personaliza tu consentimiento
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 font-mono">
                        Tu decisión es revocable cuando quieras desde /legal/cookies
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-900 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3 text-sm">
                  <Category
                    locked
                    on
                    title="Esenciales"
                    desc="Necesarias para autenticación, sesión y protección CSRF. No pueden desactivarse."
                  />
                  <Category
                    on={prefs.preferences}
                    onToggle={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
                    title="Preferencias"
                    testid="cookie-toggle-preferences"
                    desc="Recuerdan tu idioma, configuración y decisiones (incluyendo este banner)."
                  />
                  <Category
                    on={prefs.analytics}
                    onToggle={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                    title="Analíticas"
                    testid="cookie-toggle-analytics"
                    desc="Google Analytics 4 con IP anonimizada. Solo nos dice cuántas personas visitan cada página, nunca quién."
                  />
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <button
                    onClick={rejectAll}
                    data-testid="cookie-reject-all-detailed"
                    className="px-5 py-2.5 bg-white hover:bg-gray-50 text-[#0B2447] text-sm font-semibold border border-[#0B2447]"
                  >
                    Rechazar todo
                  </button>
                  <button
                    onClick={savePrefs}
                    data-testid="cookie-save-prefs"
                    className="px-5 py-2.5 bg-[#0B2447] hover:bg-[#071932] text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
                  >
                    Guardar mis preferencias <Check className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Category({ title, desc, on, onToggle, locked, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 border border-gray-200 p-3 bg-gray-50/50">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 text-sm">{title}</div>
        <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{desc}</div>
      </div>
      <button
        type="button"
        disabled={locked}
        onClick={() => !locked && onToggle && onToggle(!on)}
        data-testid={testid}
        aria-pressed={on}
        className={`shrink-0 w-12 h-6 relative transition-colors ${
          on ? "bg-[#0B2447]" : "bg-gray-300"
        } ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${
            on ? "translate-x-6" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

/** Helper for the rest of the app to check whether the visitor opted in to analytics. */
export function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return false;
    return !!parsed.analytics;
  } catch {
    return false;
  }
}
